/**
 * Data manipulation and formatting utilities
 */
/**
 * Merge CSS class names, filtering out falsy values
 */
function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
}
/**
 * Create a limited stack data structure
 */
class LimitedStack {
    items = [];
    maxSize;
    constructor(maxSize) {
        this.maxSize = maxSize;
    }
    push(item) {
        this.items.push(item);
        if (this.items.length > this.maxSize) {
            this.items.shift();
        }
    }
    pop() {
        return this.items.pop();
    }
    peek() {
        return this.items[this.items.length - 1];
    }
    get length() {
        return this.items.length;
    }
    toArray() {
        return [...this.items];
    }
    clear() {
        this.items = [];
    }
}
/**
 * Sanitize MongoDB query to prevent injection
 */
function sanitizeMongoQuery(query, allowedFields) {
    const sanitized = {};
    for (const [key, value] of Object.entries(query)) {
        if (allowedFields.includes(key)) {
            // Remove any MongoDB operators that could be dangerous
            if (typeof value === 'object' && value !== null) {
                const sanitizedValue = {};
                for (const [innerKey, innerValue] of Object.entries(value)) {
                    // Only allow safe MongoDB operators
                    if ([
                        '$eq',
                        '$ne',
                        '$in',
                        '$nin',
                        '$gt',
                        '$gte',
                        '$lt',
                        '$lte',
                    ].includes(innerKey)) {
                        sanitizedValue[innerKey] = innerValue;
                    }
                }
                if (Object.keys(sanitizedValue).length > 0) {
                    sanitized[key] = sanitizedValue;
                }
            }
            else {
                sanitized[key] = value;
            }
        }
    }
    return sanitized;
}
/**
 * Deep clone an object
 */
function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }
    if (obj instanceof Date) {
        return new Date(obj.getTime());
    }
    if (obj instanceof Array) {
        return obj.map((item) => deepClone(item));
    }
    if (typeof obj === 'object') {
        const cloned = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                cloned[key] = deepClone(obj[key]);
            }
        }
        return cloned;
    }
    return obj;
}
/**
 * Check if two objects are deeply equal
 */
function deepEqual(a, b) {
    if (a === b)
        return true;
    if (a instanceof Date && b instanceof Date) {
        return a.getTime() === b.getTime();
    }
    if (!a || !b || (typeof a !== 'object' && typeof b !== 'object')) {
        return a === b;
    }
    if (a === null || a === undefined || b === null || b === undefined) {
        return false;
    }
    if (a.prototype !== b.prototype)
        return false;
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) {
        return false;
    }
    return keys.every((k) => deepEqual(a[k], b[k]));
}
/**
 * Flatten nested object into dot notation
 */
function flattenObject(obj, prefix = '') {
    const flattened = {};
    for (const [key, value] of Object.entries(obj)) {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.assign(flattened, flattenObject(value, newKey));
        }
        else {
            flattened[newKey] = value;
        }
    }
    return flattened;
}
/**
 * Convert flat object back to nested structure
 */
function unflattenObject(obj) {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
        const keys = key.split('.');
        let current = result;
        for (let i = 0; i < keys.length - 1; i++) {
            const k = keys[i];
            if (!(k in current)) {
                current[k] = {};
            }
            current = current[k];
        }
        current[keys[keys.length - 1]] = value;
    }
    return result;
}
/**
 * Pick specific keys from an object
 */
function pick(obj, keys) {
    const result = {};
    for (const key of keys) {
        if (key in obj) {
            result[key] = obj[key];
        }
    }
    return result;
}
/**
 * Omit specific keys from an object
 */
function omit(obj, keys) {
    const result = { ...obj };
    for (const key of keys) {
        delete result[key];
    }
    return result;
}

export { LimitedStack, classNames, deepClone, deepEqual, flattenObject, omit, pick, sanitizeMongoQuery, unflattenObject };
//# sourceMappingURL=data.esm.js.map
